"use client";

import { useId, useState } from "react";

const FAQS = [
  {
    question: "What is PitchBuddy?",
    answer:
      "PitchBuddy is a speaking practice app that helps you practice pitches, presentations, interviews, and everyday conversations. It gives you feedback so you can improve how you sound over time.",
  },
  {
    question: "Who is PitchBuddy for?",
    answer:
      "PitchBuddy is for founders, job seekers, creators, students, and anyone who wants to speak more clearly and confidently.",
  },
  {
    question: "What kind of feedback does PitchBuddy give?",
    answer:
      "PitchBuddy helps you improve areas like clarity, confidence, pace, energy, filler words, and overall speaking delivery.",
  },
  {
    question: "Can I practice without writing my own pitch first?",
    answer:
      "Yes. You can generate a pitch inside the app or practice using guided drills and other speaking modes.",
  },
  {
    question: "Is PitchBuddy only for startup pitches?",
    answer:
      "No. You can use PitchBuddy to practice intros, presentations, interviews, updates, and other speaking scenarios too.",
  },
  {
    question: "Do I need to practice every day?",
    answer:
      "No. But regular short practice sessions usually lead to better speaking improvement over time.",
  },
] as const;

export default function FaqSection() {
  const baseId = useId().replace(/:/g, "");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id="faq"
      className="pt-6 pb-14 sm:pt-7 sm:pb-16 lg:pt-8 lg:pb-18"
      aria-labelledby="faq-title"
    >
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400">
          FAQ
        </p>
        <h2
          id="faq-title"
          className="mt-2.5 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl"
        >
          Frequently asked questions
        </h2>
        <p className="mt-2.5 text-base leading-7 text-muted-foreground sm:text-lg">
          Everything you may want to know before you try PitchBuddy.
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-3xl sm:mt-9">
        <div className="overflow-hidden rounded-2xl border border-border/65 bg-background/80 shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_8px_28px_rgba(0,0,0,0.04)]">
          {FAQS.map((item, index) => {
            const isOpen = openIndex === index;
            const panelId = `${baseId}-faq-panel-${index}`;
            const triggerId = `${baseId}-faq-trigger-${index}`;

            return (
              <div key={item.question} className="border-b border-border/55 last:border-b-0">
                <h3 className="text-base font-semibold leading-snug text-foreground">
                  <button
                    type="button"
                    id={triggerId}
                    className={`flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left transition-colors sm:px-5 sm:py-4 ${
                      isOpen ? "bg-primary/[0.04]" : "hover:bg-muted/30"
                    }`}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() =>
                      setOpenIndex((prev) => (prev === index ? null : index))
                    }
                  >
                    <span className="pr-2">{item.question}</span>
                    <span
                      aria-hidden="true"
                      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ease-out ${
                        isOpen
                          ? "rotate-180 border-primary/35 bg-primary/10 text-primary"
                          : "rotate-0 border-border/60 bg-background/80 text-muted-foreground"
                      }`}
                    >
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div
                    className="min-h-0 overflow-hidden"
                    aria-hidden={!isOpen}
                  >
                    <p className="px-4 pb-3.5 text-sm leading-6 text-muted-foreground/95 sm:px-5 sm:pb-4 sm:text-[0.9375rem] sm:leading-7">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
